/**
 * Product hooks (S1.4) — house list stack + store-first mutations.
 *
 * - `useProductsTable` drives the rebuilt ProductListPage via useAntdTable
 *   (pre-first-fetch `loading || params.length === 0` guard, store echo).
 *   Category filtering: ONE selected node goes server-side (`categoryId`,
 *   the handler includes the whole subtree); MULTIPLE nodes union their
 *   subtrees client-side over a full mock-scale fetch so pagination totals
 *   stay correct (the mock list endpoint accepts a single categoryId only).
 * - The detail read uses a static `/api/`-prefixed cacheKey; every mutation
 *   invalidates it via exact-key `clearCache` (the callback form is a silent
 *   no-op — see useTaxonomy). `productVendorsCacheKey` stays in that
 *   invalidation set although no hook populates it any more: vendor lines
 *   arrive NESTED on the detail payload (`ProductWireDetail`), never over a
 *   read of their own. There is no vendor-line hook here for the same reason —
 *   `useProductVendors` was deleted 2026-08-10 having never had a consumer,
 *   and a hook that describes a read path the page does not take is worse than
 *   no hook at all.
 * - SCN linking is a retroactive PATCH (`scn` on UpdateProductRequest):
 *   link/join/unlink never touch past documents — those keep their own
 *   references (derive-never-restore).
 */

import { useMemo } from 'react';
import { clearCache, useAntdTable, useRequest } from 'ahooks';
import { productApi, purchaseOrderApi, salesOrderApi } from '@/services/api';
import { useProductStore } from '@/stores/productStore';
import type { ProductFilters } from '@/stores/productStore';
import { subtreeIds, useTaxonomyTree } from '@/hooks/useTaxonomy';
import { marketOfSubsidiary } from '@/lib/scn';
import type {
  Product,
  ProductListParams,
  ProductVendorLine,
  UpdateProductRequest,
} from '@/types/api/product';
import type { ScnSpecGroup } from '@/types/api/scn';

// ---------------------------------------------------------------------------
// Cache keys (static, /api/-prefixed — house rule)
// ---------------------------------------------------------------------------

export const productDetailCacheKey = (id: string) => `/api/products/${id}`;
export const productVendorsCacheKey = (id: string) => `/api/products/${id}/vendors`;
export const productSalesStatsCacheKey = (id: string) => `/api/products/${id}/sales-stats`;
const PRODUCT_TX_SO_CACHE_KEY = '/api/sales-orders?view=product-transactions';
const PRODUCT_TX_PO_CACHE_KEY = '/api/purchase-orders?view=product-transactions';

/** Invalidate every cache a product mutation can stale (exact keys only). */
export function invalidateProductCaches(...productIds: Array<string | undefined>) {
  const keys = productIds
    .filter((id): id is string => Boolean(id))
    .flatMap((id) => [productDetailCacheKey(id), productVendorsCacheKey(id)]);
  clearCache([...keys, ...PRODUCT_OPTIONS_CACHE_KEYS]);
}

// ---------------------------------------------------------------------------
// Options (all-rows dropdown/lookup feeds — mirrors useVendorOptions)
// ---------------------------------------------------------------------------

/** Filter variants for the all-rows product options fetch. */
export type ProductOptionsVariant = 'all' | 'ready' | 'scn-linked' | 'scn-unlinked';

const PRODUCT_OPTIONS_VARIANTS: Record<
  ProductOptionsVariant,
  { cacheKey: string; params: ProductListParams }
> = {
  // Includes INACTIVE products — demand pool + CBM dims enrich past documents.
  all: { cacheKey: '/api/products-options', params: {} },
  // Active Ready only — the two-tier readiness gate (SO/import line pickers).
  ready: {
    cacheKey: '/api/products-options?readiness=ready',
    params: { readiness: 'ready', isActive: true },
  },
  // Includes INACTIVE members — SCN groups show a group's full history.
  'scn-linked': { cacheKey: '/api/products-options?hasScn=true', params: { hasScn: true } },
  // Active only — inactive products can't join a new SCN group.
  'scn-unlinked': {
    cacheKey: '/api/products-options?hasScn=false',
    params: { hasScn: false, isActive: true },
  },
};

const PRODUCT_OPTIONS_CACHE_KEYS = Object.values(PRODUCT_OPTIONS_VARIANTS).map(
  (variant) => variant.cacheKey
);

/**
 * All products matching a variant, for dropdowns/lookups (never paginated UI).
 * `pageSize: -1` = every row (API spec) — a fixed cap would silently truncate
 * once the catalogue outgrows it. Pass a literal variant; the cacheKey is
 * fixed at first render (ahooks), so it must not change across renders.
 */
export function useProductOptions(variant: ProductOptionsVariant = 'all') {
  const { cacheKey, params } = PRODUCT_OPTIONS_VARIANTS[variant];
  const { data, error, loading, run } = useRequest(
    () => productApi.list({ ...params, pageSize: -1 }),
    {
      cacheKey,
      staleTime: 60000, // 1 minute cache
    }
  );

  const products = useMemo(() => data?.data ?? [], [data]);

  return {
    products,
    error,
    isLoading: loading,
    // Raw response — consumers gating on "fetch landed" (useDemandPool)
    data,
    /** Invalidate + re-run (never bare ahooks refresh in components). */
    refresh: () => {
      clearCache(cacheKey);
      return run();
    },
  };
}

// ---------------------------------------------------------------------------
// List (useAntdTable — S0.3 list-kit contract)
// ---------------------------------------------------------------------------

/** Mock-scale ceiling for the client-side multi-category union fetch. */
const UNION_FETCH_PAGE_SIZE = 500;

export function useProductsTable(filters: ProductFilters) {
  const { setProducts, setPagination } = useProductStore();
  const { nodes } = useTaxonomyTree();

  const selectedCategoryIds = filters.categoryPaths
    .map((path) => path[path.length - 1])
    .filter(Boolean);

  const baseParams = (): ProductListParams => ({
    search: filters.search.trim() || undefined,
    readiness: filters.readiness === 'all' ? undefined : filters.readiness,
    hasScn: filters.scnLink === 'all' ? undefined : filters.scnLink === 'linked',
    isActive: filters.isActive === null ? undefined : filters.isActive,
  });

  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize }) => {
      if (selectedCategoryIds.length <= 1) {
        const response = await productApi.list({
          ...baseParams(),
          categoryId: selectedCategoryIds[0],
          page: current,
          pageSize,
        });
        setProducts(response.data);
        setPagination(response.pagination);
        return { total: response.pagination.total, list: response.data };
      }

      // Multiple category nodes: union of subtrees, paginated client-side
      const response = await productApi.list({
        ...baseParams(),
        page: 1,
        pageSize: UNION_FETCH_PAGE_SIZE,
      });
      const allowed = new Set<string>();
      for (const id of selectedCategoryIds) {
        for (const nodeId of subtreeIds(nodes, id)) allowed.add(nodeId);
      }
      const rows = response.data.filter((p) => allowed.has(p.categoryId));
      const start = (current - 1) * pageSize;
      const pageRows = rows.slice(start, start + pageSize);
      setProducts(pageRows);
      setPagination({
        page: current,
        pageSize,
        total: rows.length,
        totalPages: Math.ceil(rows.length / pageSize) || 1,
      });
      return { total: rows.length, list: pageRows };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [
        filters.search,
        JSON.stringify(filters.categoryPaths),
        filters.readiness,
        filters.scnLink,
        filters.isActive,
        // Multi-category union needs the taxonomy tree; refetch once it lands
        selectedCategoryIds.length > 1 ? nodes.length : 0,
      ],
    }
  );

  return {
    tableProps,
    refresh,
    // Pre-first-fetch guard (house rule) — never flash the empty state
    isLoading: loading || params.length === 0,
    error,
  };
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

export function useProduct(id?: string) {
  const { data, error, loading, run } = useRequest(() => productApi.getById(id!), {
    ready: !!id,
    refreshDeps: [id],
    cacheKey: id ? productDetailCacheKey(id) : undefined,
  });

  return {
    product: data?.data,
    error,
    isLoading: loading,
    // Raw-response aliases (ProductFormWizard destructures { data, loading })
    data,
    loading,
    /** Invalidate + re-run (never bare ahooks refresh in components). */
    refresh: () => {
      if (id) invalidateProductCaches(id);
      return run();
    },
  };
}

// ---------------------------------------------------------------------------
// SCN groups (Link/Join drawer data)
// ---------------------------------------------------------------------------

export interface ScnGroup {
  scn: string;
  members: Product[];
}

export function useScnGroups() {
  const { products, error, isLoading, refresh } = useProductOptions('scn-linked');

  const groups: ScnGroup[] = [];
  for (const product of products) {
    if (!product.scn) continue;
    const existing = groups.find((g) => g.scn === product.scn);
    if (existing) existing.members.push(product);
    else groups.push({ scn: product.scn, members: [product] });
  }
  groups.sort((a, b) => a.scn.localeCompare(b.scn));

  return { groups, error, isLoading, refresh };
}

/** Active products WITHOUT an SCN — partner candidates for new-group linking. */
export function useScnCandidates() {
  const { products, error, isLoading } = useProductOptions('scn-unlinked');

  return { candidates: products, error, isLoading };
}

// Next free SCN code — moved to the pure lib (S7.1); re-exported so existing
// consumers (ScnLinkDrawer) keep their import path. ONE implementation only.
export { nextScnCode } from '@/lib/scn';

// ---------------------------------------------------------------------------
// SCN 選擇式關聯 selection model (S7.3 — the board-approved candidate list).
//
// The OEM/SCN board pins the drawer's selection list to FIVE columns —
// 既有產品公司料號 / OEM所屬國家市場 / OEM Number / SCN / NOTE — searchable
// across all five (「也要允許用搜尋的」), and INCLUDING not-yet-linked
// candidates (the worked example lists 產品C/產品D with blank SCN alongside
// the linked 產品A/產品F). Selecting a row with an SCN joins that group;
// selecting an unlinked row starts a new group with that partner.
//
// Row-building and the search predicate are PURE (zero I/O, dataset from the
// caller — the src/lib purity precedent) so the drawer .tsx only renders.
// ---------------------------------------------------------------------------

/** One row of the 選擇式關聯 selection list (the board's five columns). */
export interface ScnLinkCandidateRow {
  /** Product id — selection target. */
  id: string;
  /** 既有產品公司料號 — company part number (sku; internalProductId echoed). */
  sku: string;
  internalProductId: string;
  /** OEM所屬國家市場 — subsidiary-derived market (TW / AU …). */
  market: string;
  /** OEM Number — null renders '-' (list-kit dash rule). */
  oemNo: string | null;
  /** SCN — null for not-yet-linked candidates (blank on the board). */
  scn: string | null;
  /** NOTE — 商品註解 first, else the first 商品備註 entry. */
  note: string | null;
  isActive: boolean;
}

/**
 * Build the selection rows from a products collection. Pure. Excludes the
 * product being linked (self) and members of its CURRENT group (joining the
 * own group is a no-op; the pre-S7.3 groupOptions filter, preserved).
 * Linked rows sort first (grouped by SCN), then unlinked candidates by sku.
 */
export function buildScnLinkRows(
  products: readonly Product[],
  self: Pick<Product, 'id' | 'scn'>
): ScnLinkCandidateRow[] {
  const rows = new Map<string, ScnLinkCandidateRow>();
  for (const product of products) {
    if (product.id === self.id) continue;
    if (self.scn && product.scn === self.scn) continue;
    if (rows.has(product.id)) continue;
    rows.set(product.id, {
      id: product.id,
      sku: product.sku,
      internalProductId: product.internalProductId,
      market: marketOfSubsidiary(product.subsidiaryId),
      oemNo: product.oemNo ?? null,
      scn: product.scn ?? null,
      note: product.itemComment?.trim() || product.productNotes?.[0]?.text || null,
      isActive: product.isActive,
    });
  }
  return [...rows.values()].sort((a, b) => {
    if (!!a.scn !== !!b.scn) return a.scn ? -1 : 1; // linked groups first
    const byScn = (a.scn ?? '').localeCompare(b.scn ?? '');
    return byScn !== 0 ? byScn : a.sku.localeCompare(b.sku);
  });
}

/**
 * Search predicate across ALL FIVE board columns (case-insensitive substring;
 * 料號 counts both mirrors the cell displays: sku AND internalProductId).
 * Empty/whitespace terms match everything.
 */
export function matchesScnLinkRow(row: ScnLinkCandidateRow, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return [
    row.sku,
    row.internalProductId,
    row.market,
    row.oemNo ?? '',
    row.scn ?? '',
    row.note ?? '',
  ].some((value) => value.toLowerCase().includes(needle));
}

/** Case-insensitive "this SCN code is already a group" check (manual entry). */
export function scnCodeExists(
  groups: ReadonlyArray<{ scn: string }>,
  code: string
): boolean {
  const needle = code.trim().toLowerCase();
  return groups.some((group) => group.scn.toLowerCase() === needle);
}

/**
 * Spec-completeness of a Spec Master group (S7.3 hint when linked): the
 * group exists only through member links until 規格基本屬性 are authored
 * (specName / majorCategory / specDescription — the seeded ScnSpecMeta
 * fields). Undefined/null group (still loading, fetch error) is NOT
 * incomplete — the hint is a progressive enhancement, never a blocker.
 */
export function isScnSpecIncomplete(group?: ScnSpecGroup | null): boolean {
  if (!group) return false;
  return !group.specName || !group.majorCategory || !group.specDescription;
}

/**
 * Candidate rows for the ScnLinkDrawer selection list: union of the linked
 * pool (scn-linked variant — includes inactive members, group history) and
 * the unlinked pool (scn-unlinked — active only; inactive products can't
 * join a new group). Both feeds are the cached options variants.
 */
export function useScnLinkCandidateRows(self: Pick<Product, 'id' | 'scn'>) {
  const linked = useProductOptions('scn-linked');
  const unlinked = useProductOptions('scn-unlinked');

  const rows = useMemo(
    () => buildScnLinkRows([...linked.products, ...unlinked.products], self),
    [linked.products, unlinked.products, self]
  );

  return {
    rows,
    isLoading: linked.isLoading || unlinked.isLoading,
    error: linked.error || unlinked.error,
  };
}

export const scnGroupSpecCacheKey = (scn: string) => `/api/scn-groups/${scn}`;

/**
 * Spec Master context for one SCN group (`GET /scn-groups/:scn`, S7.1) —
 * feeds the drawer's join-mode context panel and the ScnSection
 * spec-completeness hint. Served through `productApi.scnGroupSpec`, a NAMED
 * HOLE (register §0g): no `scnGroup(scn)` root is on the SDL, so this read
 * stays mock-side — but it goes through the facade, so it consults the flip
 * and is counted rather than hidden.
 */
export function useScnGroupSpec(scn?: string | null) {
  const { data, error, loading, run } = useRequest(
    async () => {
      const response = await productApi.scnGroupSpec(String(scn));
      return response;
    },
    {
      ready: !!scn,
      refreshDeps: [scn],
      cacheKey: scn ? scnGroupSpecCacheKey(scn) : undefined,
      staleTime: 30000,
    }
  );

  return {
    group: data?.data,
    error,
    isLoading: loading,
    /** Invalidate + re-run (never bare ahooks refresh in components). */
    refresh: () => {
      if (scn) clearCache(scnGroupSpecCacheKey(scn));
      return run();
    },
  };
}

// ---------------------------------------------------------------------------
// Transactions lens (交易紀錄 — read-only drill grid on the detail page)
// ---------------------------------------------------------------------------

export interface ProductTransactionRow {
  key: string;
  docType: 'so' | 'po';
  docId: string;
  docNumber: string;
  counterpartyName: string;
  date: string;
  qty: number;
  unitAmount: number;
  currency: string;
  status: string;
}

export function useProductTransactions(productId?: string) {
  const so = useRequest(() => salesOrderApi.list({ pageSize: 100 }), {
    ready: !!productId,
    cacheKey: PRODUCT_TX_SO_CACHE_KEY,
  });
  const po = useRequest(() => purchaseOrderApi.list({ pageSize: 100 }), {
    ready: !!productId,
    cacheKey: PRODUCT_TX_PO_CACHE_KEY,
  });

  const rows: ProductTransactionRow[] = [];
  if (productId) {
    for (const order of so.data?.data ?? []) {
      for (const line of order.lines) {
        if (line.productId !== productId) continue;
        rows.push({
          key: `so-${order.id}-${line.id}`,
          docType: 'so',
          docId: order.id,
          docNumber: order.soNumber,
          counterpartyName: order.customerName,
          date: order.orderDate,
          qty: line.qty,
          unitAmount: line.unitPrice,
          currency: line.currency,
          status: order.status,
        });
      }
    }
    for (const order of po.data?.data ?? []) {
      for (const line of order.lines) {
        if (line.productId !== productId) continue;
        rows.push({
          key: `po-${order.id}-${line.id}`,
          docType: 'po',
          docId: order.id,
          docNumber: order.poNumber,
          counterpartyName: order.vendorName,
          date: order.orderDate,
          qty: line.qty,
          unitAmount: line.unitCost,
          currency: line.currency,
          status: order.status,
        });
      }
    }
    rows.sort((a, b) => b.date.localeCompare(a.date));
  }

  return {
    rows,
    isLoading: so.loading || po.loading,
    error: so.error || po.error,
  };
}

// ---------------------------------------------------------------------------
// Sales stats (SR2.3 — 銷售分析 tiles + monthly series for SR3.3, and the
// 覆蓋天數 demand rate for SR3.1). Wraps the SR1.3 shared selector server-side;
// this hook only fetches + surfaces state.
// ---------------------------------------------------------------------------

/**
 * Per-product sales stats (SR2.3, plan §WR2). LOADING + hard-ERROR are exposed
 * UP FRONT — the consuming 銷售分析 section (SR3.3) renders skeleton / retry off
 * these without re-deriving state, and the empty payload for a no-history
 * product is a DEFINED value (all-zero stats), never an error. `ready`-gated so
 * `isLoading` stays true until the first fetch resolves for a real productId.
 * Transactional freshness (short staleTime) — sales history moves as SOs
 * confirm. Served through `productApi.salesStats`, a NAMED HOLE (register
 * §0g): the live `productSalesStats` root is a WAIT_API stub that carries
 * none of the ten fields this section renders, so binding it would empty the
 * surface. The read stays mock-side and is counted at the facade.
 */
export function useProductSalesStats(productId?: string) {
  const { data, error, loading, run } = useRequest(
    async () => {
      const response = await productApi.salesStats(String(productId));
      return response;
    },
    {
      ready: !!productId,
      refreshDeps: [productId],
      cacheKey: productId ? productSalesStatsCacheKey(productId) : undefined,
      staleTime: 30000,
    }
  );

  return {
    stats: data?.data,
    asOf: data?.asOf,
    error,
    isLoading: loading,
    /** Invalidate + re-run (never bare ahooks refresh in components). */
    refresh: () => {
      if (productId) clearCache(productSalesStatsCacheKey(productId));
      return run();
    },
  };
}

// ---------------------------------------------------------------------------
// Mutations (store-first: update Zustand echo + exact-key clearCache; the
// calling surface re-runs its own wrapped refresh — never bare refresh())
// ---------------------------------------------------------------------------

/** Row-level mutations for the list page (id varies per row). */
export function useProductListMutations() {
  const { updateProductInList } = useProductStore();

  // Both directions ride the SAME setter. The reactivate half used to go
  // through `update({ isActive: true })`, which the wire cannot carry (D-3) —
  // see `productApi.setActive`.
  const toggleActive = async (product: Product): Promise<Product> => {
    const response = await productApi.setActive(product.id, !product.isActive);
    updateProductInList(response.data.id, response.data);
    invalidateProductCaches(product.id);
    return response.data;
  };

  return { toggleActive };
}

export function useProductMutations(productId?: string) {
  const { updateProductInList } = useProductStore();

  const updateProduct = async (body: UpdateProductRequest): Promise<Product> => {
    const response = await productApi.update(productId!, body);
    updateProductInList(response.data.id, response.data);
    invalidateProductCaches(productId);
    return response.data;
  };

  // Both directions ride the SAME setter — see `toggleActive` above (D-3).
  const setActive = async (isActive: boolean): Promise<Product> => {
    const response = await productApi.setActive(productId!, isActive);
    updateProductInList(response.data.id, response.data);
    invalidateProductCaches(productId);
    return response.data;
  };

  /** Retroactive SCN link/join — past documents keep their references. */
  const linkScn = async (scn: string): Promise<Product> => updateProduct({ scn });

  /** Retroactive unlink — clears the group pointer ONLY (round-trip safe). */
  const unlinkScn = async (): Promise<Product> => updateProduct({ scn: null });

  /** Attach a partner product to `scn` (new-group mode links both sides). */
  const linkPartnerScn = async (partnerProductId: string, scn: string): Promise<Product> => {
    const response = await productApi.update(partnerProductId, { scn });
    updateProductInList(response.data.id, response.data);
    invalidateProductCaches(partnerProductId);
    return response.data;
  };

  const addVendorLine = async (body: {
    vendorId: number;
    vendorName: string;
    unitCost: number;
    currency?: string;
    /** 訂購單位 (W7-B3) — display-only capture, no conversion. */
    orderUnit?: string;
    isPreferred?: boolean;
    vendorSku?: string;
  }): Promise<ProductVendorLine> => {
    const response = await productApi.addVendor(productId!, body);
    invalidateProductCaches(productId);
    return response.data;
  };

  const setPrimaryVendor = async (vendorId: number): Promise<Product> => {
    const response = await productApi.setPreferredVendor(productId!, { vendorId });
    updateProductInList(response.data.id, response.data);
    invalidateProductCaches(productId);
    return response.data;
  };

  return {
    updateProduct,
    setActive,
    linkScn,
    unlinkScn,
    linkPartnerScn,
    addVendorLine,
    setPrimaryVendor,
  };
}
